import { Stack } from '@src/common/components/Stack';
import { WeightInput } from '@src/common/components/WeightInput';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { FormDimensionInput } from '@src/orders/PermitFormSections/FormDimensionInput';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import { AxleOverallWeightName } from '@src/permitField/AxleInformationFields';
import {
  overallDimensionsFields,
  OverallEFFOverhangField,
  OverallFrontOverhangField,
  OverallHeightField,
  OverallLengthField,
  OverallRearOverhangField,
  OverallWeightField,
  OverallWidthField,
} from '@src/permitField/OverallDimensionsFields';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { fields: string[] } & CollapseListener;

export const OverallDimensionsSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();

  const {
    control,
    formState: { errors },
  } = useFormContext<FormEntries<typeof props.fields>>();

  const { sectionInError } = useSectionError(
    Object.keys(overallDimensionsFields),
  );

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('overallDimensions')}
      id="load-overall-dimensions-section"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={OverallLengthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={OverallLengthField.fieldName}
              disabled={isFieldDisabled(OverallLengthField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={OverallWidthField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={OverallWidthField.fieldName}
              disabled={isFieldDisabled(OverallWidthField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={OverallHeightField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={OverallHeightField.fieldName}
              disabled={isFieldDisabled(OverallHeightField.fieldName)}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={OverallWeightField.fieldName}
          >
            <Controller
              control={control}
              name={OverallWeightField.fieldName}
              render={({ field, fieldState: { error: fieldError } }) => (
                <WeightInput
                  {...field}
                  disabled={isFieldDisabled(OverallWeightField.fieldName)}
                  value={field.value as number | null}
                  className={fullWidth}
                  label={t(`fields.${OverallWeightField.fieldName}`)}
                  multiplyByThousand={true}
                  error={
                    fieldError
                      ? t(`formErrors.${fieldError!.message}`)
                      : errors[AxleOverallWeightName] &&
                        t(
                          `formErrors.${errors[AxleOverallWeightName]!.message}`,
                        )
                  }
                />
              )}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={OverallFrontOverhangField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={OverallFrontOverhangField.fieldName}
              disabled={isFieldDisabled(OverallFrontOverhangField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={OverallRearOverhangField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={OverallRearOverhangField.fieldName}
              disabled={isFieldDisabled(OverallRearOverhangField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={OverallEFFOverhangField.fieldName}
          >
            <FormDimensionInput
              control={control}
              fieldName={OverallEFFOverhangField.fieldName}
              disabled={isFieldDisabled(OverallEFFOverhangField.fieldName)}
            />
          </ConditionalField>
        </Stack>
      </Stack>
    </EditOrderCollapsible>
  );
};

const fullWidth = css({
  flex: 1,
});
